/**
 * Fase 5 — La liquidación del chofer.
 *
 * Es la pantalla que paga sueldos, así que está armada para que NADA se
 * cierre sin que una persona haya visto la cuenta completa:
 *
 *  · Primero la foto del mes: todos los choferes y qué le toca a cada uno.
 *  · Después, chofer por chofer, la cuenta abierta: viaje por viaje y ticket
 *    por ticket. Es lo mismo que va a leer el chofer.
 *  · Recién ahí el botón de cerrar, que echa la llave para siempre.
 *
 * Si el chofer debe más de lo que ganó, el botón no aparece: aparece el
 * motivo y la salida (diferir el saldo con un ajuste).
 */

import { formatearPesos, formatearFecha, formatearKm } from './formato.js';

const $ = (sel, dentro = document) => dentro.querySelector(sel);

function alerta(tipo, texto) {
  const simbolos = { critico: '!', aviso: '!', bien: '✓' };
  const div = document.createElement('div');
  div.className = `alerta ${tipo}`;
  div.setAttribute('role', tipo === 'critico' ? 'alert' : 'status');
  div.innerHTML = `<span class="simbolo" aria-hidden="true">${simbolos[tipo] ?? '·'}</span><span></span>`;
  div.lastElementChild.textContent = texto;
  return div;
}

function aCentavos(texto) {
  const limpio = String(texto ?? '').replace(/\./g, '').replace(',', '.').trim();
  if (limpio === '') return '0';
  if (!/^-?\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const negativo = limpio.startsWith('-');
  const [entero, decimal = ''] = limpio.replace('-', '').split('.');
  return `${negativo ? '-' : ''}${entero}${decimal.padEnd(2, '0')}`;
}

const periodoAnterior = () => {
  const h = new Date();
  h.setMonth(h.getMonth() - 1);
  return `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`;
};

const TIPOS_AJUSTE = {
  ajuste_flete_periodo_anterior: 'Se corrigió una tarifa de un mes anterior',
  gasto_cargado_tarde: 'Un gasto que se cargó tarde',
  adelanto_no_registrado: 'Un adelanto que no se había registrado',
  saldo_a_rendir_diferido: 'Se pasa el saldo a rendir al mes que viene',
  otro: 'Otro motivo',
};

export async function pantallaDeLiquidacion(contenedor, { pedir, rol }) {
  contenedor.innerHTML = '';
  const marco = document.createElement('div');
  marco.innerHTML = `
    <div class="tarjeta" style="margin-bottom:16px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:14px;flex-wrap:wrap">
        <div>
          <h2 style="font-size:16px;margin:0 0 3px">Liquidación de choferes</h2>
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0">
            Se liquida contra el viaje TERMINADO, no contra la factura ni el cobro. Si el cliente
            paga tarde, el chofer cobra igual.</p>
        </div>
        <div class="campo" style="margin:0">
          <label for="lq-periodo">Mes</label>
          <input type="month" id="lq-periodo" value="${periodoAnterior()}">
        </div>
      </div>
      <div data-rol="resumen" style="margin-top:14px"></div>
    </div>
    <div data-rol="detalle"></div>`;
  contenedor.appendChild(marco);

  const cajaResumen = $('[data-rol="resumen"]', marco);
  const cajaDetalle = $('[data-rol="detalle"]', marco);
  const campoPeriodo = $('#lq-periodo', marco);
  const periodo = () => campoPeriodo.value || periodoAnterior();

  let ajustes = [];

  async function refrescar() {
    cajaDetalle.innerHTML = '';
    const r = await pedir(`/api/liquidaciones?periodo=${periodo()}`);
    if (!r.ok) {
      cajaResumen.innerHTML = '';
      cajaResumen.appendChild(alerta('critico', r.cuerpo?.error ?? 'No se pudo traer el mes.'));
      return;
    }
    dibujarResumen(r.cuerpo.choferes ?? []);
  }

  function dibujarResumen(filas) {
    if (filas.length === 0) {
      cajaResumen.innerHTML =
        '<p style="color:var(--texto-suave);margin:0">No hay choferes cargados.</p>';
      return;
    }
    const aPagar = filas.reduce((s, c) => s + BigInt(c.neto_a_pagar_cents), 0n);

    cajaResumen.innerHTML = `
      <div class="tabla-scroll"><table class="datos">
        <thead><tr>
          <th>Chofer</th><th class="numero">Viajes</th><th class="plata">Base del mes</th>
          <th class="plata">Saldo a rendir</th><th class="plata">A pagar</th><th>Estado</th><th></th>
        </tr></thead>
        <tbody>${filas.map((c) => {
          const cerrada = c.estado === 'cerrada' || c.estado === 'pagada';
          const frenada = c.se_puede_cerrar === false;
          return `<tr data-chofer="${c.chofer_id}" data-liq="${c.liquidacion_id ?? ''}">
            <td>${c.chofer_nombre}</td>
            <td class="numero">${c.cantidad_viajes}</td>
            <td class="plata">${formatearPesos(c.base_total_cents)}</td>
            <td class="plata" ${BigInt(c.saldo_a_rendir_cents) > 0n
              ? 'style="color:var(--grave)"' : ''}>${formatearPesos(c.saldo_a_rendir_cents)}</td>
            <td class="plata" style="font-weight:600">${formatearPesos(c.neto_a_pagar_cents)}</td>
            <td>${cerrada
              ? `<span class="etiqueta bien">Cerrada</span>`
              : frenada
                ? '<span class="etiqueta critico">No se puede cerrar</span>'
                : '<span class="etiqueta">Sin cerrar</span>'}</td>
            <td><button type="button" class="boton" data-ver="${c.chofer_id}">Ver la cuenta</button></td>
          </tr>`;
        }).join('')}</tbody>
        <tfoot><tr>
          <td colspan="4"><b>Total a pagar en el mes</b></td>
          <td class="plata"><b>${formatearPesos(aPagar.toString())}</b></td>
          <td colspan="2"></td>
        </tr></tfoot>
      </table></div>`;

    for (const b of cajaResumen.querySelectorAll('[data-ver]')) {
      b.addEventListener('click', () => {
        const fila = b.closest('tr');
        ajustes = [];
        void verCuenta(b.dataset.ver, fila.dataset.liq || null);
      });
    }
  }

  // --- La cuenta de un chofer -------------------------------------------
  async function verCuenta(choferId, liquidacionId) {
    if (liquidacionId) return verCerrada(liquidacionId);

    const r = await pedir('/api/liquidaciones/vista-previa', { method: 'POST',
      body: JSON.stringify({ chofer_id: choferId, periodo: periodo(), ajustes }) });
    if (!r.ok) {
      cajaDetalle.innerHTML = '';
      cajaDetalle.appendChild(alerta('critico',
        r.cuerpo?.errores?.[0]?.problema ?? r.cuerpo?.error ?? 'No se pudo calcular.'));
      return;
    }
    dibujarCuenta(r.cuerpo, choferId, false);
  }

  async function verCerrada(liquidacionId) {
    const r = await pedir(`/api/liquidaciones/${liquidacionId}`);
    if (!r.ok) return;
    const l = r.cuerpo.liquidacion;
    dibujarCuenta({
      chofer_nombre: l.chofer_nombre, periodo: l.periodo,
      base_total_cents: l.base_total_cents,
      importe_porcentaje_cents: l.importe_porcentaje_cents,
      adelantos_recibidos_cents: l.adelantos_recibidos_cents,
      rendido_empresa_cents: l.rendido_empresa_cents,
      saldo_a_rendir_cents: l.saldo_a_rendir_cents,
      ajustes_cents: l.ajustes_cents, neto_a_pagar_cents: l.neto_a_pagar_cents,
      cantidad_viajes: l.cantidad_viajes, km_totales: l.km_totales,
      pendiente_de_cobro_cents: l.pendiente_de_cobro_cents,
      viajes: r.cuerpo.viajes.map((v) => ({
        numero: v.numero_viaje, cliente_nombre: v.cliente_nombre, origen: v.origen,
        destino: v.destino, fecha_fin: v.fecha_fin, km: v.km,
        flete_neto_cents: v.flete_neto_cents, importe_chofer_cents: v.importe_chofer_cents,
      })),
      gastos_rendidos: r.cuerpo.gastos.map((g) => ({
        fecha: g.fecha, tipo: g.tipo_gasto_nombre, proveedor: g.proveedor_nombre,
        comprobante: g.comprobante_numero, total_cents: g.total_cents,
      })),
      ajustes_guardados: r.cuerpo.ajustes,
      alertas: [], se_puede_cerrar: false,
    }, null, true, l);
  }

  function dibujarCuenta(v, choferId, cerrada, cabecera) {
    const avisos = (v.alertas ?? []).map((a) =>
      `<div class="alerta ${a.gravedad === 'freno' ? 'critico' : 'aviso'}">
         <span class="simbolo" aria-hidden="true">!</span><span>${a.texto}</span></div>`).join('');

    cajaDetalle.innerHTML = `
      <div class="tarjeta" data-rol="papel">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:14px;flex-wrap:wrap">
          <div>
            <h2 style="font-size:17px;margin:0 0 2px">${v.chofer_nombre}</h2>
            <p style="color:var(--texto-suave);font-size:13px;margin:0">
              Liquidación de ${v.periodo} · ${v.cantidad_viajes} viaje(s) ·
              ${formatearKm(Math.round(Number(v.km_totales) * 10))} km${
              cerrada ? ` · cerrada el ${formatearFecha(cabecera.fecha_cierre)}` : ''}</p>
          </div>
          <div style="display:flex;gap:8px">
            <button type="button" class="boton" data-accion="imprimir">Imprimir</button>
            <button type="button" class="boton" data-accion="cerrar-detalle">Volver</button>
          </div>
        </div>

        ${avisos}

        <h3 style="font-size:14px;margin:16px 0 8px">Los viajes del mes</h3>
        ${v.viajes.length === 0
          ? '<p style="color:var(--texto-suave);margin:0">Ningún viaje terminado en el mes.</p>'
          : `<div class="tabla-scroll"><table class="datos">
              <thead><tr><th>Viaje</th><th>Fecha</th><th>Cliente</th><th>Recorrido</th>
                         <th class="numero">Km</th><th class="plata">Flete neto</th>
                         <th class="plata">Le toca</th></tr></thead>
              <tbody>${v.viajes.map((x) => `
                <tr><td>${x.numero}</td><td>${formatearFecha(x.fecha_fin)}</td>
                    <td>${x.cliente_nombre}</td><td>${x.origen} → ${x.destino}</td>
                    <td class="numero">${formatearKm(Math.round(Number(x.km) * 10))}</td>
                    <td class="plata">${formatearPesos(x.flete_neto_cents)}</td>
                    <td class="plata">${formatearPesos(x.importe_chofer_cents)}</td></tr>`).join('')}
              </tbody>
              <tfoot><tr><td colspan="5"><b>Total</b></td>
                <td class="plata"><b>${formatearPesos(v.base_total_cents)}</b></td>
                <td class="plata"><b>${formatearPesos(v.importe_porcentaje_cents)}</b></td>
              </tr></tfoot>
            </table></div>`}

        <h3 style="font-size:14px;margin:18px 0 8px">Lo que rindió</h3>
        ${v.gastos_rendidos.length === 0
          ? '<p style="color:var(--texto-suave);margin:0 0 8px">No rindió ningún gasto con el adelanto.</p>'
          : `<div class="tabla-scroll"><table class="datos">
              <thead><tr><th>Fecha</th><th>Concepto</th><th>Proveedor</th>
                         <th>Comprobante</th><th class="plata">Importe</th></tr></thead>
              <tbody>${v.gastos_rendidos.map((g) => `
                <tr><td>${formatearFecha(g.fecha)}</td><td>${g.tipo}</td>
                    <td>${g.proveedor ?? '—'}</td><td>${g.comprobante ?? '—'}</td>
                    <td class="plata">${formatearPesos(g.total_cents)}</td></tr>`).join('')}
              </tbody></table></div>`}

        <h3 style="font-size:14px;margin:18px 0 8px">La cuenta</h3>
        <div class="tabla-scroll"><table class="datos" style="min-width:0;max-width:520px">
          <tbody>
            <tr><td>Base del mes (fletes netos)</td>
                <td class="plata">${formatearPesos(v.base_total_cents)}</td></tr>
            <tr><td>Su porcentaje</td>
                <td class="plata">${formatearPesos(v.importe_porcentaje_cents)}</td></tr>
            <tr><td>Adelantos que recibió</td>
                <td class="plata">${formatearPesos(v.adelantos_recibidos_cents)}</td></tr>
            <tr><td>Menos lo que rindió</td>
                <td class="plata">−${formatearPesos(v.rendido_empresa_cents)}</td></tr>
            <tr><td><b>Saldo a rendir</b> (se le descuenta)</td>
                <td class="plata">−${formatearPesos(v.saldo_a_rendir_cents)}</td></tr>
            ${BigInt(v.ajustes_cents) !== 0n
              ? `<tr><td>Ajustes</td><td class="plata">${formatearPesos(v.ajustes_cents)}</td></tr>`
              : ''}
          </tbody>
          <tfoot><tr><td><b>A PAGAR AL CHOFER</b></td>
            <td class="plata" style="font-size:16px"><b>${
              formatearPesos(v.neto_a_pagar_cents)}</b></td></tr></tfoot>
        </table></div>

        ${BigInt(v.pendiente_de_cobro_cents) > 0n
          ? `<p style="color:var(--texto-suave);font-size:12.5px;margin:12px 0 0">
               De estos viajes, ${formatearPesos(v.pendiente_de_cobro_cents)} todavía no los cobró
               la empresa. El chofer cobra igual: el riesgo de cobranza es de la empresa.</p>`
          : ''}

        ${(v.ajustes_guardados ?? []).length > 0
          ? `<h3 style="font-size:14px;margin:18px 0 8px">Ajustes</h3>
             <ul style="margin:0;padding-left:18px">${v.ajustes_guardados.map((a) =>
               `<li>${formatearPesos(a.importe_cents)} — ${a.motivo}</li>`).join('')}</ul>`
          : ''}

        ${cerrada ? `
          <p style="margin:18px 0 0;font-size:12.5px;color:var(--texto-suave)">
            Esta liquidación está cerrada: no se puede modificar. Si hay que corregir algo,
            se carga como ajuste en el mes siguiente.</p>
          <div style="margin-top:34px;display:flex;gap:60px;flex-wrap:wrap">
            <div style="border-top:1px solid var(--texto);padding-top:6px;min-width:220px;font-size:12.5px">
              Firma del chofer</div>
            <div style="border-top:1px solid var(--texto);padding-top:6px;min-width:220px;font-size:12.5px">
              Firma de la empresa</div>
          </div>`
          : `<div data-rol="ajustes" style="margin-top:18px"></div>
             <div data-rol="acciones" style="margin-top:16px"></div>`}
      </div>`;

    $('[data-accion="cerrar-detalle"]', cajaDetalle).addEventListener('click', () => {
      cajaDetalle.innerHTML = '';
    });
    $('[data-accion="imprimir"]', cajaDetalle).addEventListener('click', () => window.print());

    if (!cerrada) dibujarAcciones(v, choferId);
    cajaDetalle.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function dibujarAcciones(v, choferId) {
    const cajaAjustes = $('[data-rol="ajustes"]', cajaDetalle);
    const cajaAcciones = $('[data-rol="acciones"]', cajaDetalle);

    cajaAjustes.innerHTML = `
      ${ajustes.length > 0
        ? `<h3 style="font-size:14px;margin:0 0 8px">Ajustes que se van a guardar</h3>
           <ul style="margin:0 0 10px;padding-left:18px">${ajustes.map((a, i) =>
             `<li>${formatearPesos(a.importe_cents)} — ${a.motivo}
               <button type="button" class="boton" data-sacar="${i}"
                       style="margin-left:8px;padding:1px 8px">Sacar</button></li>`).join('')}</ul>`
        : ''}
      <details>
        <summary style="cursor:pointer;font-size:13.5px">Agregar un ajuste</summary>
        <div style="border:1px solid var(--borde);border-radius:8px;padding:12px;margin-top:10px">
          <p style="color:var(--texto-suave);font-size:12.5px;margin:0 0 10px">
            Nada se arrastra solo de un mes al otro. Si una deuda pasa al mes que viene, es
            porque vos lo decidís y queda escrito por qué.</p>
          <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(190px,1fr));gap:0 14px">
            <div class="campo"><label for="aj-tipo">De qué se trata</label>
              <select id="aj-tipo">${Object.entries(TIPOS_AJUSTE).map(([k, t]) =>
                `<option value="${k}">${t}</option>`).join('')}</select></div>
            <div class="campo"><label for="aj-importe">Importe</label>
              <input type="text" id="aj-importe" inputmode="decimal">
              <div class="ayuda">Con signo menos si le descuenta.</div></div>
            <div class="campo"><label for="aj-motivo">Por qué</label>
              <input type="text" id="aj-motivo"></div>
          </div>
          <button type="button" class="boton" data-accion="agregar-ajuste"
                  style="margin-top:8px">Agregar</button>
        </div>
      </details>`;

    for (const b of cajaAjustes.querySelectorAll('[data-sacar]')) {
      b.addEventListener('click', () => {
        ajustes.splice(Number(b.dataset.sacar), 1);
        void verCuenta(choferId, null);
      });
    }
    $('[data-accion="agregar-ajuste"]', cajaAjustes).addEventListener('click', () => {
      const importe = aCentavos($('#aj-importe', cajaAjustes).value);
      const motivo = $('#aj-motivo', cajaAjustes).value.trim();
      if (importe === null || importe === '0' || motivo === '') {
        alert('Poné un importe distinto de cero y escribí por qué.');
        return;
      }
      ajustes.push({ tipo: $('#aj-tipo', cajaAjustes).value, importe_cents: importe, motivo });
      void verCuenta(choferId, null);
    });

    if (!v.se_puede_cerrar) {
      cajaAcciones.innerHTML = '';
      cajaAcciones.appendChild(alerta('critico',
        'Esta liquidación no se puede cerrar todavía. Mirá el aviso de arriba: si el chofer debe ' +
        'más de lo que ganó, se puede pasar el saldo al mes que viene con un ajuste.'));
      return;
    }
    if (rol !== 'dueno') {
      cajaAcciones.innerHTML =
        '<p style="color:var(--texto-suave);font-size:13px;margin:0">' +
        'Cerrar una liquidación la deja firme. Sólo lo puede hacer el dueño.</p>';
      return;
    }

    cajaAcciones.innerHTML =
      `<button type="button" class="boton principal" data-accion="cerrar">
         Cerrar la liquidación de ${v.chofer_nombre}</button>
       <p style="color:var(--texto-suave);font-size:12.5px;margin:8px 0 0">
         Al cerrarla queda firme y no se puede modificar. Es el papel que firma el chofer.</p>`;

    $('[data-accion="cerrar"]', cajaAcciones).addEventListener('click', async () => {
      const seguro = confirm(
        `Cerrar la liquidación de ${v.chofer_nombre} de ${v.periodo}.\n\n` +
        `A pagar: ${formatearPesos(v.neto_a_pagar_cents)}\n\n` +
        'Queda firme y no se puede modificar. ¿Seguro?');
      if (!seguro) return;
      const r = await pedir('/api/liquidaciones', { method: 'POST',
        body: JSON.stringify({ chofer_id: choferId, periodo: periodo(), ajustes }) });
      if (!r.ok) {
        alert(r.cuerpo?.error ?? 'No se pudo cerrar.');
        return;
      }
      ajustes = [];
      cajaDetalle.innerHTML = '';
      await refrescar();
    });
  }

  campoPeriodo.addEventListener('change', () => { ajustes = []; void refrescar(); });
  await refrescar();
}
